import { Button, Icon } from '@toggle/design-system';
import { useDisableScroll } from '@toggle/helpers';
import { MarketOverviewWithNews, SingleNews } from '@toggle/toggle';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import React, { useState } from 'react';
import ReactGA from 'react-ga4';
import { useTranslation } from 'react-i18next';

import { ChatDeepAnalysis } from '~/components/chat/chat-input/chat-deep-analysis/ChatDeepAnalysis';
import { RenderFeatureFlag } from '~/components/render-feature-flag/RenderFeatureFlag';
import { useMarketInsightsDrawer } from '~/hooks/use-market-insights-drawer/useMarketInsightsDrawer';
import { useChat } from '~/views/alfred/research/use-chat/useChat';
import { useChatNavigation } from '~/views/alfred/use-chat-navigation/useChatNavigation';
import { ArticleDrawerContent } from '~/widgets/news-widget/ArticleDrawerContent';

import { getMarketInsightTitle } from '../market-insight-card/utils';
import * as S from './MarketInsightDrawer.styles';

/**
 * @deprecated
 */
export const MarketInsightDrawer = ({
  insight,
}: {
  insight: MarketOverviewWithNews;
}) => {
  const { t } = useTranslation([
    'whatsNew',
    'news',
    'chart',
    'analyze',
    'countries',
  ]);
  const [selectedNews, setSelectedNews] = useState<SingleNews | undefined>();
  const [activeArticleId, setActiveArticleId] = useState<string>('');

  useDisableScroll();
  const { navigateToConversation } = useChatNavigation();
  const { closeDrawer } = useMarketInsightsDrawer();
  const postQuestion = useChat(state => state.postQuestion);

  const zonedTime = toZonedTime(new Date(insight.date), 'America/New_York');

  const onNewsPillClick = (news: SingleNews) => {
    setSelectedNews(news);
    setActiveArticleId(news.id);
  };

  const onGeneratedQuestionClick = async (question: string) => {
    const conversationId = (await postQuestion(question)) ?? '';
    navigateToConversation(conversationId);

    ReactGA.event({
      category: "What's new? question",
      action: 'Generated question clicked',
      label: `Question asked: ${question}`,
    });
  };

  const onCloseClick = () => {
    if (!!selectedNews) {
      setSelectedNews(undefined);
    } else {
      closeDrawer();
    }
  };

  const onBackgroundClick = () => {
    closeDrawer();
    setSelectedNews(undefined);
    setActiveArticleId('');
  };

  return (
    <S.StyledDrawer
      isOpen
      handleCloseClick={onCloseClick}
      customBackgroundClickHandler={onBackgroundClick}
      $isExpanded={!!selectedNews}
    >
      <S.Root>
        <S.DrawersWrapper>
          <S.MarketInsightWrapper>
            <S.Header>
              <S.IconWrapper>
                <Icon
                  iconName="Coffee"
                  size={28}
                  fillColor="var(--surface-default)"
                />
              </S.IconWrapper>
              <S.HeaderColumn>
                <S.HeaderTitle>
                  {
                    getMarketInsightTitle(insight.market_hours, t)
                      .legacyCardTitle
                  }
                </S.HeaderTitle>
                <S.HeaderSubtitle>{t('whatsNew:mtNewswires')}</S.HeaderSubtitle>
              </S.HeaderColumn>
            </S.Header>
            <S.SummaryWrapper>
              <S.SummaryText>
                {format(zonedTime, "h:mm a 'ET', d MMM, yyyy")}
              </S.SummaryText>
              <S.SummaryText>{insight.summary}</S.SummaryText>
              {insight.articles.length && (
                <>
                  <S.ReadMoreLabel>{t('whatsNew:readMoreOn')}</S.ReadMoreLabel>
                  <S.NewsPillsWrapper>
                    {insight.articles.map(news => (
                      <S.NewsPill
                        onClick={() => onNewsPillClick(news)}
                        key={news.id}
                      >
                        <S.NewsPillText>{news.title}</S.NewsPillText>
                        <S.NewsPillDot>·</S.NewsPillDot>
                        <S.NewsPillProviderText>
                          {t('news:source', {
                            context: news.source,
                          })}
                        </S.NewsPillProviderText>
                      </S.NewsPill>
                    ))}
                  </S.NewsPillsWrapper>
                </>
              )}
            </S.SummaryWrapper>
            <S.AlfredSectionWrapper>
              <S.HorizontalDivider />
              <S.AlfredSectionTitle>
                <Icon
                  iconName="Alfred"
                  size={20}
                  fillColor="var(--icon-primary)"
                />
                <span>{t('whatsNew:whatAmIMissing')}</span>
              </S.AlfredSectionTitle>
              <S.AlfredQuestionsWrapper>
                <RenderFeatureFlag
                  requiredFlag={'alfred_everywhere_feature'}
                  fallback={
                    <S.StyledChatInput>
                      <ChatDeepAnalysis
                        config={{
                          placeholder: t('whatsNew:askAFollowUp'),
                          allowedModes: ['research'],
                          disableContextHeader: true,
                          autoFocus: false,
                        }}
                      />
                    </S.StyledChatInput>
                  }
                />
                <S.GeneratedQuestionsWrapper>
                  {insight.questions.map(question => (
                    <S.GeneratedQuestionWrapper key={question}>
                      <span>{question}</span>
                      <Button
                        iconName="ArrowUp"
                        size="small"
                        onClick={() => onGeneratedQuestionClick(question)}
                      />
                    </S.GeneratedQuestionWrapper>
                  ))}
                </S.GeneratedQuestionsWrapper>
              </S.AlfredQuestionsWrapper>
            </S.AlfredSectionWrapper>
          </S.MarketInsightWrapper>
          {!!selectedNews && (
            <S.ArticleWrapper>
              <S.VerticalDivider />
              <ArticleDrawerContent
                newsId={activeArticleId}
                data={selectedNews}
              />
            </S.ArticleWrapper>
          )}
        </S.DrawersWrapper>
      </S.Root>
    </S.StyledDrawer>
  );
};
